import React, { Fragment, useState } from "react";
import PropTypes from "prop-types";
import { 
  Typography, 
  withStyles, 
  TextField, 
  Button, 
  Select, 
  MenuItem, 
  FormControl, 
  InputLabel,
  Snackbar
} from "@material-ui/core";

const styles = theme => ({
  cardContainer: {
    // ... other styles like backgroundColor, padding, borderRadius ...
    textAlign: "center", // Ensures text and inline-block children are centered
  },
  iconWrapper: {
    width: "50px",
    height: "50px",
    borderRadius: "50%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontWeight: "bold",
    fontSize: "20px",
    color: "#fff"
  },
  formField: {
    marginBottom: theme.spacing(2),
    "& .MuiOutlinedInput-root": {
      borderRadius: "8px",
      "&:hover fieldset": {
        borderColor: "#667EEA",
      },
      "&.Mui-focused fieldset": {
        borderColor: "#667EEA",
      },
    }
  },
  submitButton: {
    backgroundColor: "#667EEA",
    color: "white",
    padding: "12px 24px",
    borderRadius: "8px",
    fontSize: "16px",
    fontWeight: 600,
    fontFamily: "'Lato', sans-serif",
    textTransform: "none",
    width: "100%",
    marginTop: theme.spacing(2),
    "&:hover": {
      backgroundColor: "#5A67D8",
    },
    "&:disabled": {
      backgroundColor: "#E5E7EB",
      color: "#9CA3AF",
    }
  },
  privacyNote: {
    textAlign: "center",
    fontSize: "12px",
    color: "#6B7280",
    marginTop: theme.spacing(1),
    fontFamily: "'Lato', sans-serif",
    lineHeight: 1.4,
  }
});

function shadeColor(hex, percent) {
  const f = parseInt(hex.slice(1), 16);
  const t = percent < 0 ? 0 : 255;
  const p = percent < 0 ? percent * -1 : percent;
  const R = f >> 16;
  const G = (f >> 8) & 0x00ff;
  const B = f & 0x0000ff;
  return `#${(
    0x1000000 +
    (Math.round((t - R) * p) + R) * 0x10000 +
    (Math.round((t - G) * p) + G) * 0x100 +
    (Math.round((t - B) * p) + B)
  )
    .toString(16)
    .slice(1)}`;
}

function WaitlistCard(props) {
  const { classes, Icon, color, headline, text, fontFamily } = props;
  
  const [formData, setFormData] = useState({
    email: "",
    firstName: "",
    interest: "",
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.email) return;

    setIsSubmitting(true);
    
    try {
      // Replace 'YOUR_GOOGLE_APPS_SCRIPT_WEB_APP_URL_HERE' with the actual URL you copied
      const webAppUrl = 'https://script.google.com/macros/s/AKfycbzv2zVOOEDdFdZ-SEvixcjqG4o9c9Oas53osXgrUU8bzCENMGPNhzzt559zkvPzKDqy-g/exec'; // Your Google Apps Script web app URL
      
      const response = await fetch(webAppUrl, {
        method: 'POST',
        headers: {
          // Change Content-Type
          'Content-Type': 'text/plain', 
        },
        body: JSON.stringify(formData), 
      });

      if (response.ok) {
        // const result = await response.json(); // You can parse the response from Apps Script
        // Logger.log(result); // Log the result from Apps Script if needed
        setIsSubmitted(true); 
      } else {
        const errorResult = await response.text(); // Get error text for debugging
        console.error('Failed to submit waitlist form:', response.statusText, errorResult);
      }
      
    } catch (error) {
      console.error('Error submitting waitlist form:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isSubmitted) {
    return (
      <div className={classes.cardContainer}>
        <div
          className={classes.iconWrapper}
          style={{
            borderRadius: "50%",
            color: "#00C853",
            backgroundColor: shadeColor("#00C853", 0.5),
            fill: "#00C853",
            margin: "0 auto ", 
          }}
        >
          ✓
        </div>
        <Typography
          variant="h5"
          paragraph
          style={{
            fontFamily: "'Lato', sans-serif",
            fontSize: "50px",
            color: "#361C10", // Changed color
            fontWeight: 700,
            whiteSpace: 'nowrap' // Add this line to prevent wrapping
          }}
        >
          Welcome!
        </Typography>
        <Typography
          variant="body1"
          color="textSecondary"
          style={{
            fontFamily: "'Lato', sans-serif",
            fontSize: "20px",
            color: "#4A4A4A",
          }}
        >
          Thanks {formData.firstName || 'there'}! You're now on the Wolfe waitlist.
        </Typography>
      </div>
    );
  }

  return (
    <div className={classes.cardContainer}>
      <div
        className={classes.iconWrapper}
        style={{
          borderRadius: "50%",
          color: color,
          backgroundColor: shadeColor(color, 0.5),
          fill: color,
          margin: "0 auto", 
          marginBottom: "16px", 
        }}
      >
        {Icon}
      </div>
      
      <Typography
        variant="h5"
        paragraph
        style={{
          fontFamily: fontFamily || "'Lato', sans-serif", // Assuming fontFamily is passed from Home.js as 'Playfair Display', serif
          fontSize: "50px",
          color: "#361C10",
          fontWeight: 700,
          whiteSpace: 'nowrap' // Add this line to prevent wrapping
        }}
      >
        {headline}
      </Typography>
      
      <Typography
        variant="body1"
        color="textSecondary"
        style={{
          fontFamily: "'Lato', sans-serif",
          fontSize: "20px",
          color: "#4A4A4A",
          marginBottom: "24px"
        }}
      >
        {text}
      </Typography>

      <form onSubmit={handleSubmit} style={{ maxWidth: '500px', margin: '0 auto' }}>
        <TextField
          className={classes.formField}
          fullWidth
          required
          label="Email Address"
          name="email"
          type="email"
          value={formData.email}
          onChange={handleInputChange}
          variant="outlined"
          placeholder="your@email.com"
          size="small"
        />
        
        <TextField
          className={classes.formField}
          fullWidth
          label="First Name"
          name="firstName"
          value={formData.firstName}
          onChange={handleInputChange}
          variant="outlined"
          placeholder="Your first name"
          size="small"
        />
        
        <FormControl className={classes.formField} fullWidth variant="outlined" size="small">
          <InputLabel>Interest</InputLabel>
          <Select
            name="interest"
            value={formData.interest}
            onChange={handleInputChange}
            label="Interest"
          >
            <MenuItem value="anxiety">Anxiety & Stress</MenuItem>
            <MenuItem value="depression">Depression Support</MenuItem>
            <MenuItem value="general">General Wellness</MenuItem>
            <MenuItem value="workplace">Workplace Health</MenuItem>
            <MenuItem value="relationships">Relationships</MenuItem>
            <MenuItem value="sleep">Sleep & Relaxation</MenuItem>
            <MenuItem value="other">Other</MenuItem>
          </Select>
        </FormControl>
        
        <Button
          type="submit"
          className={classes.submitButton}
          disabled={isSubmitting || !formData.email}
        >
          {isSubmitting ? "Joining..." : "Join Waitlist"}
        </Button>
        
        <Typography className={classes.privacyNote}>
          No spam, ever. Unsubscribe anytime.
        </Typography>
      </form>
    </div>
  );
}

WaitlistCard.propTypes = {
  classes: PropTypes.object.isRequired,
  Icon: PropTypes.element.isRequired,
  color: PropTypes.string.isRequired,
  headline: PropTypes.string.isRequired,
  text: PropTypes.string.isRequired,
  fontFamily: PropTypes.string // Add propType for fontFamily
};

export default withStyles(styles, { withTheme: true })(WaitlistCard);